'use client';

import { useState } from 'react';
import ChartHeader from './ChartHeader';
import SpectrogramViewer, { type SpectrogramPanel } from './SpectrogramViewer';
import Logo from '@/components/icons/Logo';

const VIRIDIS =
  'linear-gradient(to top, #440154, #482878, #3e4a89, #31688e, #26828e, #1f9e89, #35b779, #6ece58, #b5de2b, #fde725)';

const RED =
  'linear-gradient(to top, #0a0000, #4a0000, #930000, #d40000, #ff1f1f)';

type SignalId = 'band' | 'chirp';

type SignalConfig = {
  label: string;
  subtitle: string;
  panels: SpectrogramPanel[];
  aspectClass: string;
  freqMax?: number;
  freqTicks?: number[];
  colorbarGradient: string;
  colorbarMax: number;
  colorbarTicks: number[];
  colorbarFormat?: (v: number) => string;
  colorbarLabel?: string;
};

const SIGNALS: Record<SignalId, SignalConfig> = {
  band: {
    label: 'Band-passed',
    subtitle:
      'Isolates low-frequency inharmonics the model added that are not in the recording',
    panels: [
      { id: 'recorded', src: '/stft/recorded.png', label: 'Recorded' },
      { id: 'before', src: '/stft/before.png', label: 'Before' },
      { id: 'after', src: '/stft/after.png', label: 'After' },
    ],
    aspectClass: 'aspect-[2048/820]',
    colorbarGradient: VIRIDIS,
    colorbarMax: 0.013,
    colorbarTicks: [0, 0.002, 0.004, 0.006, 0.008, 0.01, 0.012],
  },
  chirp: {
    label: 'Full chirp',
    subtitle: 'Full spectrum, unfiltered chirp from the default v3 sweep file',
    panels: [
      { id: 'before', src: '/stft/chirp-v3-before.png', label: 'Before' },
      { id: 'after', src: '/stft/chirp-v3-after.png', label: 'After' },
    ],
    aspectClass: 'aspect-[1920/1024]',
    freqMax: 24000,
    freqTicks: [0, 4000, 8000, 12000, 16000, 20000, 24000],
    colorbarGradient: RED,
    colorbarMax: 90,
    colorbarTicks: [0, 30, 60, 90],
    colorbarFormat: (v) => String(v - 90),
    colorbarLabel: 'dB',
  },
};

const SIGNAL_ORDER: SignalId[] = ['chirp', 'band'];

export default function A2Aliasing() {
  const [signal, setSignal] = useState<SignalId>('chirp');
  const cfg = SIGNALS[signal];

  const signalControl = (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500">Signal</span>
      <div className="flex items-center gap-1 rounded-lg bg-white/5 p-0.5">
        {SIGNAL_ORDER.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setSignal(id)}
            className={`rounded-md px-2.5 py-1 transition ${
              signal === id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            {SIGNALS[id].label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="Aliasing-like Artifacts"
        description="Sine sweeps run through the old and new model designs. Full chirp is the full spectrum, unfiltered. Band-passed zooms in on low-frequency inharmonics the model invented (absent from the recording)."
      />
      <SpectrogramViewer
        key={signal}
        title="STFT Magnitude Spectrograms"
        subtitle={cfg.subtitle}
        panels={cfg.panels}
        defaultView="before"
        aspectClass={cfg.aspectClass}
        freqMax={cfg.freqMax}
        freqTicks={cfg.freqTicks}
        colorbarGradient={cfg.colorbarGradient}
        colorbarMax={cfg.colorbarMax}
        colorbarTicks={cfg.colorbarTicks}
        colorbarFormat={cfg.colorbarFormat}
        colorbarLabel={cfg.colorbarLabel}
        extraControl={signalControl}
      />
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
